import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { STORAGE_URL } from '../../services/api';
import { 
  Save, Search, CheckCircle, 
  Trash2, Edit, Eye, X 
} from 'lucide-react';
// 👇 1. Common Layout aur PageTitle import kiya gaya hai
import AdminLayout from '../layouts/AdminLayout'; 
import PageTitle from '../components/ui/PageTitle';

const AdminTopCompanies = () => {
  const [name, setName] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const [viewCompany, setViewCompany] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  const adminToken = localStorage.getItem('admin_token');

  const fetchCompanies = async () => {
    setTableLoading(true);
    try {
      const response = await api.get("/top-companies");
      setCompanies(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching companies:", error);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !logoFile) return alert("Enter name and select logo.");
    setLoading(true);
    const formData = new FormData();
    formData.append('name', name);
    formData.append('logo', logoFile);
    formData.append('status', 1); 

    try {
      await api.post('/top-companies', formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${adminToken}` }
      });
      setSuccess(true);
      setName('');
      setLogoFile(null);
      e.target.reset();
      fetchCompanies();
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      alert("Upload failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = (currentStatus == 1 || currentStatus === 'active') ? 0 : 1;
    setCompanies(companies.map(c => c.id === id ? { ...c, status: newStatus } : c));

    try {
      await api.post(`/top-companies-status/${id}`, 
      { status: newStatus }, 
      { headers: { 'Authorization': `Bearer ${adminToken}` } });
    } catch (error) {
      console.error(error);
      alert("Failed to update status");
      fetchCompanies(); 
    }
  };

  const handleView = (company) => {
    setViewCompany(company);
    setIsViewOpen(true);
  };

  const openEdit = (company) => {
    setEditData({ id: company.id, name: company.name, logoFile: null, preview: company.logo });
    setIsEditOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    const formData = new FormData();
    formData.append('name', editData.name);
    if (editData.logoFile) {
      formData.append('logo', editData.logoFile);
    }
    try {
      await api.post(`/top-companies-update/${editData.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${adminToken}` }
      });
      fetchCompanies();
      setIsEditOpen(false);
    } catch (error) {
      console.error(error);
      alert("Failed to update company.");
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete?")) {
      try {
        await api.delete(`/top-companies/${id}`, {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        });
        setCompanies(companies.filter(c => c.id !== id));
      } catch (error) {
        alert("Failed to delete.");
      }
    }
  };

  const filteredCompanies = companies.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()));

  // 👇 2. Yahan manually likhe gaye Header/Sidebar ko hata kar <AdminLayout> use kiya gaya hai
  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto space-y-8">
        
        {/* 👇 3. Common PageTitle Use kiya gaya hai */}
        <div className="mb-2">
          <PageTitle 
            title="Manage Top Companies" 
            subtitle="Add and manage companies displayed on the homepage." 
          />
        </div>

        {/* --- Add New Company Section --- */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          <h2 className="text-lg font-bold text-gray-800 mb-6 border-b pb-3 border-gray-100">Add New Company</h2>
          {success && (
            <div className="bg-green-50 text-green-700 p-4 rounded-xl mb-6 flex items-center text-sm font-medium border border-green-100">
              <CheckCircle className="w-5 h-5 mr-2 text-green-500" /> Company added successfully!
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#FED547] focus:ring-1 focus:ring-[#FED547]" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company Logo *</label>
                <div className="w-full border border-gray-200 rounded-xl px-2 py-1.5 bg-white flex items-center">
                  <input type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files[0])} className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:font-semibold file:bg-yellow-50 file:text-yellow-700 hover:file:bg-yellow-100 cursor-pointer" required />
                </div>
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <button type="submit" disabled={loading} className="flex items-center gap-2 bg-[#FED547] text-black px-6 py-2.5 rounded-lg font-bold hover:bg-[#e5be3c]">
                <Save className="w-4 h-4" /> {loading ? 'Saving...' : 'Save Company'}
              </button>
            </div>
          </form>
        </div>

        {/* --- Added Companies List Section --- */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100 flex justify-between gap-4 bg-gray-50/50">
            <h2 className="text-lg font-bold text-gray-800">Added Companies</h2>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" placeholder="Search company..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="border border-gray-200 rounded-full pl-9 pr-4 py-2 text-sm w-64 focus:outline-none focus:border-[#FED547] focus:ring-1" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-600">
              <thead className="bg-[#FED547] text-black font-bold">
                <tr>
                  <th className="py-4 px-6 uppercase tracking-wider text-xs w-16">S.No</th>
                  <th className="py-4 px-6 uppercase tracking-wider text-xs">Logo</th>
                  <th className="py-4 px-6 uppercase tracking-wider text-xs">Company Name</th>
                  <th className="py-4 px-6 uppercase tracking-wider text-xs">Status</th>
                  <th className="py-4 px-6 uppercase tracking-wider text-xs text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tableLoading ? (
                  <tr><td colSpan="5" className="py-10 text-center text-gray-400">Loading...</td></tr>
                ) : filteredCompanies.map((company, index) => (
                  <tr key={company.id} className="hover:bg-gray-50">
                    <td className="py-3 px-6">{index + 1}</td>
                    <td className="py-3 px-6">
                      <div className="w-12 h-12 bg-white border border-gray-100 rounded-md p-1 shadow-sm flex justify-center items-center">
                        <img src={STORAGE_URL(company.logo)} alt={company.name} className="max-w-full max-h-full object-contain" />
                      </div>
                    </td>
                    <td className="py-3 px-6 font-semibold text-gray-800">{company.name}</td>
                    
                    <td className="py-3 px-6">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleStatus(company.id, company.status)}
                          className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors focus:outline-none ${
                            company.status == 1 || company.status === 'active' ? 'bg-green-500' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                              company.status == 1 || company.status === 'active' ? 'translate-x-5' : 'translate-x-1'
                            }`}
                          />
                        </button>
                        <span className={`text-xs font-bold ${company.status == 1 || company.status === 'active' ? 'text-green-600' : 'text-gray-400'}`}>
                          {company.status == 1 || company.status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </td>

                    <td className="py-3 px-6">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => handleView(company)} className="p-1.5 text-gray-400 hover:text-blue-500 rounded"><Eye className="w-4 h-4" /></button>
                        <button onClick={() => openEdit(company)} className="p-1.5 text-gray-400 hover:text-yellow-500 rounded"><Edit className="w-4 h-4" /></button>
                        <button onClick={() => handleDelete(company.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      {/* View Modal */}
      {isViewOpen && viewCompany && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm relative shadow-2xl animate-in zoom-in-95">
            <button onClick={() => setIsViewOpen(false)} className="absolute top-4 right-4 text-gray-400 hover:text-black"><X className="w-5 h-5"/></button>
            <h3 className="text-lg font-bold text-gray-900 border-b pb-3 mb-4">Company Details</h3>
            <div className="flex flex-col items-center">
              <div className="w-32 h-32 bg-gray-50 border border-gray-100 rounded-xl p-4 flex items-center justify-center mb-4">
                <img src={STORAGE_URL(viewCompany.logo)} alt={viewCompany.name} className="max-w-full max-h-full object-contain" />
              </div>
              <h4 className="text-xl font-extrabold text-gray-800">{viewCompany.name}</h4>
              <span className={`mt-2 px-3 py-1 rounded-full text-xs font-bold ${viewCompany.status == 1 || viewCompany.status === 'active' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                {viewCompany.status == 1 || viewCompany.status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditOpen && editData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md relative shadow-2xl animate-in zoom-in-95">
            <button onClick={() => setIsEditOpen(false)} className="absolute top-4 right-4 text-gray-400 hover:text-black"><X className="w-5 h-5"/></button>
            <h3 className="text-lg font-bold text-gray-900 border-b pb-3 mb-4">Edit Company</h3>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                <input type="text" value={editData.name} onChange={(e) => setEditData({...editData, name: e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FED547]" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Update Logo (Optional)</label>
                <input type="file" accept="image/*" onChange={(e) => setEditData({...editData, logoFile: e.target.files[0]})} className="w-full text-sm text-gray-500 file:mr-4 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-yellow-50 file:text-yellow-700" />
              </div>
              <button type="submit" disabled={editLoading} className="w-full bg-[#FED547] text-black font-bold py-2.5 rounded-lg mt-4 disabled:opacity-70">
                {editLoading ? 'Updating...' : 'Save Changes'}
              </button>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminTopCompanies;